// AdminViolationsPage — Real-time monitor of accounts approaching/exceeding daily loss
// Polls every 30s (simulated). Color-coded by severity.

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface ViolationAccount {
  id: string;
  trader: string;
  avatar: number;
  account: string;
  tier: 'Beginner' | 'Mid' | 'Professional';
  accountSize: number;
  dailyLossUsed: number;
  dailyLossLimit: number;
  pct: number;
  status: 'safe' | 'caution' | 'warning' | 'critical' | 'breached';
  lastTrade: string;
  totalPnl: number;
  openPositions: number;
}

const ACCOUNTS: ViolationAccount[] = [
  { id: 'a1', trader: 'Marcus J.', avatar: 1, account: 'FP-100K-14283', tier: 'Mid',          accountSize: 100000, dailyLossUsed: 380,  dailyLossLimit: 2000,  pct: 19,  status: 'safe',     lastTrade: '2m ago',  totalPnl: 1840, openPositions: 1 },
  { id: 'a2', trader: 'Sarah C.',  avatar: 2, account: 'FP-150K-12844', tier: 'Professional', accountSize: 150000, dailyLossUsed: 1240, dailyLossLimit: 3000,  pct: 41,  status: 'caution',  lastTrade: '5m ago',  totalPnl: 4280, openPositions: 3 },
  { id: 'a3', trader: 'Riley T.',  avatar: 3, account: 'FP-50K-15201',  tier: 'Beginner',     accountSize: 50000,  dailyLossUsed: 880,  dailyLossLimit: 1000,  pct: 88,  status: 'warning',  lastTrade: '12m ago', totalPnl: 320,  openPositions: 2 },
  { id: 'a4', trader: 'Devon K.',  avatar: 4, account: 'FP-100K-13892', tier: 'Mid',          accountSize: 100000, dailyLossUsed: 1880, dailyLossLimit: 2000,  pct: 94,  status: 'critical', lastTrade: '18m ago', totalPnl: -680, openPositions: 4 },
  { id: 'a5', trader: 'Felix W.',  avatar: 1, account: 'FP-50K-14672',  tier: 'Beginner',     accountSize: 50000,  dailyLossUsed: 1020, dailyLossLimit: 1000,  pct: 102, status: 'breached', lastTrade: '25m ago', totalPnl: -1020, openPositions: 0 },
  { id: 'a6', trader: 'Ana M.',    avatar: 2, account: 'FP-150K-13256', tier: 'Professional', accountSize: 150000, dailyLossUsed: 2150, dailyLossLimit: 3000,  pct: 72,  status: 'warning',  lastTrade: '1h ago',  totalPnl: 1840, openPositions: 5 },
  { id: 'a7', trader: 'Brent O.',  avatar: 3, account: 'FP-100K-14821', tier: 'Mid',          accountSize: 100000, dailyLossUsed: 920,  dailyLossLimit: 2000,  pct: 46,  status: 'caution',  lastTrade: '1h ago',  totalPnl: 420,  openPositions: 2 },
  { id: 'a8', trader: 'Lisa H.',   avatar: 4, account: 'FP-50K-15034',  tier: 'Beginner',     accountSize: 50000,  dailyLossUsed: 180,  dailyLossLimit: 1000,  pct: 18,  status: 'safe',     lastTrade: '2h ago',  totalPnl: 1240, openPositions: 1 },
];

const STATUS_META: Record<string, { color: string; bg: string; label: string; icon: string }> = {
  safe:     { color: '#34C759', bg: 'rgba(52,199,89,0.15)',  label: 'Safe',     icon: '✓' },
  caution:  { color: '#FFD60A', bg: 'rgba(255,214,10,0.15)', label: 'Caution',  icon: '⚠️' },
  warning:  { color: '#FF9500', bg: 'rgba(255,149,0,0.15)',  label: 'Warning',  icon: '⚠️' },
  critical: { color: '#FF3B30', bg: 'rgba(255,59,48,0.15)',  label: 'Critical', icon: '🚨' },
  breached: { color: '#FF3B30', bg: 'rgba(255,59,48,0.3)',   label: 'Breached', icon: '⛔' },
};

const TIER_COLOR = { Beginner: '#34C759', Mid: '#0A84FF', Professional: '#1C1C1E' };

export function AdminViolationsPage() {
  const nav = useNavigate();
  const [accounts, setAccounts] = useState(ACCOUNTS);
  const [filter, setFilter] = useState<'all' | 'critical' | 'breached' | 'warning'>('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => {
      setLastRefresh(new Date());
      setAccounts((curr) => curr.map((a) => ({
        ...a,
        dailyLossUsed: Math.max(0, a.dailyLossUsed + (Math.random() - 0.5) * 60),
        pct: 0,
      })));
      setAccounts((curr) => curr.map((a) => ({ ...a, pct: Math.round((a.dailyLossUsed / a.dailyLossLimit) * 100) })));
    }, 5000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  const filtered = accounts.filter((a) => filter === 'all' || a.status === filter);
  const breached = accounts.filter((a) => a.status === 'breached').length;
  const critical = accounts.filter((a) => a.status === 'critical').length;
  const warning  = accounts.filter((a) => a.status === 'warning').length;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-white/10 bg-[#0A0A0A]/80 backdrop-blur-xl px-2">
        <button onClick={() => nav('/admin/dashboard')} data-testid="back" className="flex h-9 w-9 items-center justify-center text-[#0A84FF] haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Violations monitor</div>
        <button onClick={() => setAutoRefresh(!autoRefresh)} data-testid="autorefresh" className={['px-3 text-[15px]', autoRefresh ? 'text-[#34C759]' : 'text-white/40'].join(' ')}>
          {autoRefresh ? '● Live' : '○ Paused'}
        </button>
      </div>

      {/* Alert banner */}
      {(breached > 0 || critical > 0) && (
        <div className="mx-4 mt-3 rounded-[14px] p-3 text-center" style={{ background: 'linear-gradient(135deg, #FF3B30 0%, #B82B22 100%)' }} data-testid="alert-banner">
          <div className="text-[11px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.85)' }}>🚨 Active alerts</div>
          <div className="mt-0.5 text-[18px] font-bold">{breached} breached · {critical} critical</div>
        </div>
      )}

      {/* Stats */}
      <div className="px-4 pt-3 grid grid-cols-4 gap-2">
        <Stat label="Tracked"  value={accounts.length} color="#0A84FF" />
        <Stat label="Breached" value={breached}        color="#FF3B30" />
        <Stat label="Critical" value={critical}        color="#FF9500" />
        <Stat label="Warning"  value={warning}         color="#FFD60A" />
      </div>

      <div className="px-4 mt-2 text-[10px] text-white/40 text-right">Updated {lastRefresh.toLocaleTimeString()}</div>

      {/* Filter tabs */}
      <div className="px-4 pt-3 flex items-center gap-1.5 overflow-x-auto">
        {(['all', 'critical', 'breached', 'warning'] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`vfilter-${f}`}
            className={['shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold capitalize transition',
              filter === f ? 'bg-white text-black' : 'bg-white/10 text-white/70 active:opacity-40'
            ].join(' ')}
          >{f}</button>
        ))}
      </div>

      {/* Account list */}
      <div className="px-4 mt-3 space-y-2">
        {filtered.map((a) => {
          const s = STATUS_META[a.status];
          return (
            <div key={a.id} data-testid={`v-${a.id}`} className="rounded-[14px] border border-white/10 bg-white/5 p-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[14px] font-bold" style={{ backgroundColor: TIER_COLOR[a.tier] + '30', color: TIER_COLOR[a.tier] }}>{a.trader[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[14px] font-bold text-white">{a.trader}</span>
                    <span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase" style={{ backgroundColor: s.bg, color: s.color }}>{s.icon} {s.label}</span>
                  </div>
                  <div className="text-[11px] text-white/50 font-mono">{a.account} · ${(a.accountSize/1000).toFixed(0)}K {a.tier}</div>
                </div>
                <div className="text-right">
                  <div className="text-[16px] font-bold tabular-nums" style={{ color: s.color }}>{a.pct}%</div>
                  <div className="text-[9px] text-white/40">${a.dailyLossUsed.toLocaleString()} / ${a.dailyLossLimit.toLocaleString()}</div>
                </div>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                <div className="h-full rounded-full" style={{ width: `${Math.min(a.pct, 100)}%`, backgroundColor: s.color }} />
              </div>
              <div className="mt-2 flex items-center justify-between text-[10px] text-white/50">
                <span>Last trade {a.lastTrade} · {a.openPositions} open pos</span>
                <div className="flex gap-2">
                  <button onClick={() => alert('Sent warning to trader')} data-testid={`warn-${a.id}`} className="text-[#FF9500] active:opacity-40">⚠ Warn</button>
                  <button onClick={() => alert('Killed all open positions')} data-testid={`kill-${a.id}`} className="text-[#FF3B30] active:opacity-40">⛔ Kill</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
      <div className="text-[9px] text-white/60 uppercase">{label}</div>
      <div className="text-[16px] font-bold" style={{ color }}>{value}</div>
    </div>
  );
}
